/** The two header actions every data place has: Add and Import - ADR-0212 decision 10.
 *
 * **Offered only to a role that may enter data** (`mayEnterData`, the route table's own upload
 * roles). For a Viewer or an Auditor the component renders nothing at all: a control that predictably
 * fails is the defect UX-04 names, and the role could not use it any other way, so there is no reason
 * to explain a disabled one. A control is shown disabled **with its reason beneath it** only for a
 * role that *may* enter data when this particular place cannot take it yet (Scope 3's Add: "Not
 * available yet"), because that person can act in other ways and should be told why this one is not
 * open.
 *
 * Weight (KD, 2026-11-01): **Add is the page-level action and solid teal**; Import is secondary
 * (white, teal text, border). Both are `ActionButton`.
 *
 * It decides nothing else. What Add opens (a short side drawer) and what Import does (the existing
 * spreadsheet round trip) are the place's: it passes `onAdd` and `onImport`. A place with no Import
 * today (Vendors) passes neither `onImport` nor `importUnavailable`, and no Import is drawn.
 */

import { DownloadSimple, Plus } from "@phosphor-icons/react";

import { mayEnterData } from "../../shell/routeTable";
import { ActionButton } from "./ActionButton";
import styles from "./AddImportActions.module.css";

export interface AddImportActionsProps {
  /** The signed-in role (`useMe().data?.role`). Absent or unknown: nothing is drawn. */
  role: string | undefined;
  onAdd?: () => void;
  /** The place cannot take an Add now. The button is drawn disabled and this is its reason. */
  addUnavailable?: string;
  addLabel?: string;
  /** A stable name for the Add control (spelled `testId` so the gate's scan of rendered ids sees it). */
  testId?: string;
  onImport?: () => void;
  importUnavailable?: string;
  importLabel?: string;
}

export function AddImportActions({
  role,
  onAdd,
  addUnavailable,
  addLabel = "Add",
  testId,
  onImport,
  importUnavailable,
  importLabel = "Import",
}: AddImportActionsProps) {
  if (!mayEnterData(role)) return null;
  const hasAdd = onAdd !== undefined || addUnavailable !== undefined;
  const hasImport = onImport !== undefined || importUnavailable !== undefined;
  if (!hasAdd && !hasImport) return null;
  return (
    <div className={styles.actions} role="group" aria-label="Add or import data">
      {hasImport ? (
        <ActionButton data-action="import" reason={importUnavailable} onClick={onImport}>
          <DownloadSimple size={14} weight="bold" aria-hidden="true" />
          {importLabel}
        </ActionButton>
      ) : null}
      {hasAdd ? (
        <ActionButton
          variant="primary"
          data-action="add"
          {...(testId ? { "data-testid": testId } : {})}
          reason={addUnavailable}
          onClick={onAdd}
        >
          <Plus size={14} weight="bold" aria-hidden="true" />
          {addLabel}
        </ActionButton>
      ) : null}
    </div>
  );
}
